import * as React from 'react';

import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

import { useMediaQuery } from '@/hooks';
import { cn } from '@/utils';

const ScrollArea = React.forwardRef(
	(
		{
			autoHide = false,
			className,
			children,
			maxHeight = 200,
			orientation = 'vertical',
			maxWidth,
			onlyDesktop,
			scrollBarClassName,
			viewportProps,
			...props
		},
		ref
	) => {
		const sm = useMediaQuery('sm');

		if (onlyDesktop && sm) {
			return (
				<div className={className} ref={ref} {...props}>
					{children}
				</div>
			);
		}

		return (
			<ScrollAreaPrimitive.Root
				ref={ref}
				className={cn('relative', !sm && 'overflow-hidden', className)}
				{...props}
			>
				<ScrollAreaPrimitive.Viewport
					style={{ maxHeight, maxWidth }}
					className={cn(
						'w-full rounded-[inherit]',
						viewportProps?.className || ''
					)}
					{...viewportProps}
				>
					{children}
				</ScrollAreaPrimitive.Viewport>
				{['vertical', 'both'].includes(orientation) && (
					<ScrollBar
						orientation="vertical"
						className={scrollBarClassName}
						forceMount={!autoHide}
					/>
				)}
				{['horizontal', 'both'].includes(orientation) && (
					<ScrollBar
						orientation="horizontal"
						className={scrollBarClassName}
						forceMount={!autoHide}
					/>
				)}
				<ScrollAreaPrimitive.Corner />
			</ScrollAreaPrimitive.Root>
		);
	}
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef(
	({ className, orientation = 'vertical', ...props }, ref) => (
		<ScrollAreaPrimitive.ScrollAreaScrollbar
			ref={ref}
			orientation={orientation}
			className={cn(
				'flex touch-none select-none transition-colors',
				['vertical', 'both'].includes(orientation) &&
					'h-full w-2.5 border-l border-l-transparent p-[1px]',
				['horizontal', 'both'].includes(orientation) &&
					'h-2.5 flex-col border-t border-t-transparent p-[1px]',
				className
			)}
			{...props}
		>
			<ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-grey-200" />
		</ScrollAreaPrimitive.ScrollAreaScrollbar>
	)
);
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export default ScrollArea;

export { ScrollArea, ScrollBar };
